import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, SITE } from "@/lib/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding] duration-500 ${scrolled || open ? "bg-ink/85 backdrop-blur-xl py-3" : "bg-transparent py-5"}`}>
      <div className="container-x flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" aria-label="Austyle Plumbing home"><Logo light /></Link>
        <nav className="hidden lg:flex items-center gap-5 xl:gap-8">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.id || n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold tracking-wide transition-colors link-underline pb-1 ${isActive ? "text-brass" : "text-white/85 hover:text-white"}`}>
              {n.id === "24-7" && <span className="h-1.5 w-1.5 rounded-full bg-brass animate-pulse" />}
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a href={SITE.tel} data-testid="header-call-button" className="hidden xl:flex items-center gap-2 text-white font-mono text-sm px-4 py-2.5 border border-white/20 rounded-full hover:border-brass hover:text-brass transition-colors">
            <Phone className="h-4 w-4" /> {SITE.phone}
          </a>
          <Link to="/book" data-testid="header-book-button" className="group flex items-center gap-2 bg-brass text-ink font-bold text-sm px-5 py-2.5 rounded-full hover:bg-white transition-colors">
            Book online <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden text-white p-2 -mr-2" aria-label="Toggle menu">
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav data-testid="mobile-menu" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4 }} className="lg:hidden overflow-hidden">
            <div className="container-x pt-6 pb-8 flex flex-col gap-1">
              {[...NAV, { to: "/book", label: "Book online" }].map((n, i) => (
                <motion.div key={n.to} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
                  <NavLink to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${n.id || n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) => `block font-wide font-extrabold text-3xl py-3 border-b border-white/10 ${isActive ? "text-brass" : "text-white"}`}>
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
